import React, { useState } from 'react';
import { FileDown, Loader2, SlidersHorizontal, Trash2, UserCog, X } from 'lucide-react';
import { toast } from 'sonner';
import { bulkAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { exportRowsToCsv } from '../../utils/csvExport';
import BulkDeleteDialog from './BulkDeleteDialog';
import BulkStatusDialog from './BulkStatusDialog';
import BulkAssignDialog from './BulkAssignDialog';

// Mirrors ENTITIES in backend/routers/bulk_ops.py
export const BULK_MODULES = {
  parts: { label: 'parts', statuses: ['active', 'inactive'], reasonRequired: [] },
  costings: {
    label: 'costings',
    statuses: ['draft', 'pending_approval', 'approved', 'rejected'],
    reasonRequired: ['rejected'],
  },
  quotations: {
    label: 'quotations',
    statuses: ['draft', 'pending_approval', 'approved', 'released', 'open', 'won', 'lost', 'expired', 'cancelled'],
    reasonRequired: ['lost'],
  },
  orders: {
    label: 'sales orders',
    statuses: ['draft', 'confirmed', 'in_production', 'completed', 'cancelled'],
    reasonRequired: ['cancelled'],
  },
  inquiries: {
    label: 'inquiries',
    statuses: ['new', 'under_review', 'costing_in_progress', 'open', 'quoted', 'follow_up', 'won', 'lost', 'closed', 'cancelled'],
    reasonRequired: ['lost', 'closed', 'cancelled'],
  },
  process_flows: { label: 'process flows', statuses: ['active', 'inactive'], reasonRequired: [] },
  customers: { label: 'customers', statuses: ['active', 'inactive'], reasonRequired: [] },
};

/**
 * Sticky action bar shown while rows are selected: export, bulk status change
 * (manager/admin) and recoverable bulk delete (admin only).
 */
export function BulkActionBar({
  entity, selectedIds, selectedRows = [], onClear, onDone, csvColumns = [], csvPrefix,
}) {
  const { user } = useAuth();
  const config = BULK_MODULES[entity];
  const count = selectedIds?.size || 0;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  if (count === 0 || !config) return null;

  const ids = Array.from(selectedIds);
  const isAdmin = user?.role === 'admin';
  const canStatus = isAdmin || user?.role === 'manager';

  const reportSkipped = (skipped) => {
    if (skipped?.length) {
      toast.warning(`${skipped.length} skipped: ${skipped.slice(0, 3).map((row) => `${row.reference} (${row.reason})`).join('; ')}`);
    }
  };

  const openDelete = async () => {
    setBusy(true);
    try {
      const response = await bulkAPI.previewDelete(entity, ids);
      setPreview(response.data);
      setDeleteOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not check these records');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (confirm) => {
    setBusy(true);
    try {
      const response = await bulkAPI.delete(entity, ids, confirm);
      toast.success(response.data.message);
      reportSkipped(response.data.skipped);
      setDeleteOpen(false);
      onClear?.();
      onDone?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmStatus = async (status, reason) => {
    setBusy(true);
    try {
      const response = await bulkAPI.status(entity, ids, status, reason);
      toast.success(response.data.message);
      reportSkipped(response.data.skipped);
      setStatusOpen(false);
      onClear?.();
      onDone?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk status change failed');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    if (selectedRows.length === 0) {
      toast.error('Selected rows are no longer loaded — reselect them to export');
      return;
    }
    const exported = exportRowsToCsv(selectedRows, csvColumns, csvPrefix || entity);
    toast.success(`Exported ${exported} ${config.label}`);
  };

  return (
    <>
      <div
        className="sticky top-2 z-20 flex flex-wrap items-center gap-2 border border-blue-200 bg-blue-50/95 px-4 py-3 backdrop-blur"
        data-testid={`bulk-action-bar-${entity}`}
      >
        <Badge className="bg-blue-700 text-white" data-testid={`bulk-selected-count-${entity}`}>
          {count} selected
        </Badge>
        <span className="text-sm text-muted-foreground">Selection is kept while you change pages.</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {csvColumns.length > 0 && (
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid={`bulk-export-${entity}`}>
              <FileDown className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
          {canStatus && (
            <Button variant="outline" size="sm" onClick={() => setStatusOpen(true)} data-testid={`bulk-status-${entity}`}>
              <SlidersHorizontal className="mr-2 h-4 w-4" /> Change status
            </Button>
          )}
          {entity === 'inquiries' && isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)} data-testid={`bulk-assign-${entity}`}>
              <UserCog className="mr-2 h-4 w-4" /> Assign owner
            </Button>
          )}
          {isAdmin && (
            <Button variant="destructive" size="sm" onClick={openDelete} disabled={busy} data-testid={`bulk-delete-${entity}`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClear} data-testid={`bulk-clear-${entity}`}>
            <X className="mr-1 h-4 w-4" /> Clear
          </Button>
        </div>
      </div>

      <BulkDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        moduleLabel={config.label}
        preview={preview}
        loading={busy}
        onConfirm={confirmDelete}
      />

      <BulkStatusDialog
        open={statusOpen}
        onOpenChange={setStatusOpen}
        moduleLabel={config.label}
        count={count}
        statuses={config.statuses}
        reasonRequired={config.reasonRequired}
        loading={busy}
        onConfirm={confirmStatus}
      />

      {entity === 'inquiries' && (
        <BulkAssignDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          ids={ids}
          onDone={() => { onClear?.(); onDone?.(); }}
        />
      )}
    </>
  );
}

export default BulkActionBar;
